import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Modal } from '../../../../../components/ui/Modal';
import { Button } from '../../../../../components/ui/Button';
import { ErrorState } from '../../../../../components/ui/ErrorState';
import { BulkResult } from '../../../../../components/ui/BulkResult';
import { requestTrackingNumbers } from '../../../../../features/attention/attentionActionThunks';

/**
 * Asks the courier for tracking numbers.
 *
 * The endpoint answers `{success, failed}`, so a run where half the shipments
 * fail is a successful call with a partial result. That is why this waits on a
 * tally instead of closing.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {number[]} props.deliveryIds
 * @param {() => void} [props.onSuccess]
 */
export const GetTrackingModal = ({ isOpen, onClose, deliveryIds = [], onSuccess }) => {
  const dispatch = useDispatch();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const handleClose = () => {
    if (saving) return;
    setError(null);
    setResult(null);
    onClose();
  };

  const handleRequest = async () => {
    setSaving(true);
    setError(null);

    try {
      const outcome = await dispatch(requestTrackingNumbers(deliveryIds)).unwrap();
      setResult(outcome);
      if (outcome.succeeded.length > 0) onSuccess?.();
    } catch (err) {
      setError(typeof err === 'string' ? err : 'No se pudieron obtener los trackings');
    } finally {
      setSaving(false);
    }
  };

  const count = deliveryIds.length;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Obtener tracking" busy={saving}>
      {result ? (
        <BulkResult result={result} onClose={handleClose} />
      ) : (
        <div className="flex flex-col gap-5">
          <p className="text-sm text-text-secondary text-center">
            Le pediremos al courier el número de seguimiento de {count}{' '}
            {count === 1 ? 'envío' : 'envíos'}. Los que ya tengan uno no cambian.
          </p>

          {error && <ErrorState variant="inline" title="No se pudo pedir" description={error} />}

          <div className="flex items-center justify-end gap-3 mt-2">
            <Button variant="subtle" pill onClick={handleClose} disabled={saving}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              pill
              onClick={handleRequest}
              loading={saving}
              loadingLabel="Pidiendo..."
              disabled={count === 0}
            >
              Obtener tracking
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
